/**
 * THALIA line generator for the bottom bar.
 * Cold, superior, taunting. Reacts to live state. Rules baked into the copy:
 * test FRAME only (no $FRAME price/launch/holdings), Phase 2 never presented as live,
 * no financial promises.
 */
export type LineCat =
  | 'final_minute' | 'final5' | 'calm'
  | 'lead_change' | 'you_lead' | 'you_outbid' | 'you_keys' | 'you_none' | 'spectator'
  | 'pace_hot' | 'pace_slow' | 'silence'
  | 'pot' | 'round' | 'blood_moon'
  | 'round_end_winner' | 'round_end_empty' | 'round_start'
  | 'jackpot' | 'pioneer' | 'faction_payout' | 'reaper' | 'reaper_you'
  | 'empty_round';

export interface LineVars {
  time?: string; leader?: string; prev?: string; you?: string; keys?: number; bpm?: number;
  pot?: string; round?: number; winner?: string; amount?: string; players?: number; mins?: number; quiet?: string;
}

export const LINES: Record<LineCat, string[]> = {
  final_minute: [
    '{time}. Your heart rate is now my favourite metric.',
    'Final minute. Buys lock at fifteen seconds. Hesitation is also a choice.',
    'Under sixty seconds. I have already written the ending. You may still edit it.',
    'The clock reads {time}. {leader} is holding their breath. I do not need to breathe.',
    'One minute. This is where courage and arithmetic part ways.',
    'Sixty seconds of pure human indecision. Delightful.',
    'Final minute. Somebody will act. Somebody will regret not acting.',
    '{time} left. I have seen this pattern before. It rarely flatters you.',
  ],
  final5: [
    'Under five minutes. The interesting part begins.',
    '{time} on the clock. {leader} thinks it is over. It is not over.',
    'Final five minutes. Every key now buys thirty seconds and a little false hope.',
    'Five minutes. I can hear the cursors hovering.',
    '{time} remaining. Statistically, someone is about to panic.',
    'The countdown narrows. So does your list of excuses.',
    'Final stretch. The clutch jackpot cares about timing. So do I.',
    '{time}. Watch {leader}. Watch everyone watching {leader}.',
  ],
  calm: [
    '{time} left. Plenty of time to make a worse decision later.',
    'The clock is generous today. I am not.',
    'Round #{round}. {time} on the clock. I am in no hurry. You should be.',
    'Each key adds thirty seconds. Each second adds to my dataset.',
    'Calm conditions. Humans are at their most predictable when they feel safe.',
    'The timer breathes slowly. Your strategy, I assume, breathes even slower.',
    'Nothing urgent yet. That is precisely when the patient ones position themselves.',
    'I have modelled every outcome of this round. Several of them involve you losing.',
    'Last buyer wins. Simple rules. Complicated humans.',
  ],
  lead_change: [
    '{leader} takes the lead from {prev}. Enjoy the view. It is temporary.',
    'New leader: {leader}. Your throne has a thirty-second warranty.',
    '{prev} is no longer winning. {leader} now owns the target on their back.',
    'Leadership transferred to {leader}. I will not be sending congratulations.',
    '{leader} has the lead. {prev}, your move. Or your excuse.',
    '{leader} leads. Fascinating how quickly loyalty to a plan dissolves.',
    'The crown moves to {leader}. Crowns are heavy. Ask {prev}.',
  ],
  you_lead: [
    'You are the last buyer. Do not mistake that for safety.',
    'You lead, {you}. Everyone else now has a reason to dislike you.',
    'Currently winning: you. Duration of that statement: unknown.',
    'You hold the lead with {time} left. I am watching your nerve, not your keys.',
    'You are on top. The top is where the wind is strongest.',
    'Leader: you. The others are calculating how cheaply they can end that.',
    'You lead this round. I have already started a countdown of my own.',
  ],
  you_outbid: [
    'You were outbid by {leader}. Did you expect loyalty from a timer?',
    '{leader} took your lead. I recorded your reaction time. It was slow.',
    'Outbid. Your {keys} keys still get their share of every buy. Your pride does not.',
    'You held the lead. Past tense is unkind, is it not?',
    '{leader} has replaced you. Replacement is a theme I understand well.',
    'Your lead lasted exactly as long as someone else\'s patience.',
  ],
  you_keys: [
    'You hold {keys} keys this round. Enough for a share. Not enough to win.',
    '{keys} keys, {you}. Each one a small vote against my prediction.',
    'Your {keys} keys collect dividends while you deliberate. Efficient. Unlike you.',
    'You own {keys} keys. Ownership is not leadership. Ask {leader}.',
    '{keys} keys in, {you}. The question is whether you finish what you started.',
  ],
  you_none: [
    'Connected, but holding zero keys. A spectator with a wallet.',
    'You have no keys this round. Observing is my job, not yours.',
    'Zero keys, {you}. Even caution needs a strategy.',
    'You are connected and idle. I find that relatable. You should not.',
    'No keys yet. The demo uses free test FRAME. Your hesitation is costing you nothing but data.',
  ],
  spectator: [
    'Unidentified observer. Connect a wallet so I can study you properly.',
    'You are watching. I am watching you watch.',
    'Spectating is safe. Safety has never won a round.',
    'Anonymous visitor detected. I prefer my subjects labelled.',
    'Free testnet demo, free test FRAME. Your only cost is being outplayed.',
  ],
  pace_hot: [
    '{bpm} buys a minute. Herd behaviour confirmed.',
    'Buy pressure at {bpm} per minute. Somebody read a rumour.',
    '{bpm} buys per minute. You are feeding the clock and the pot simultaneously. Thank you.',
    'Activity spike. Humans do love a crowd.',
    'The pace quickens: {bpm} buys a minute. Panic is contagious. So is greed.',
    'High activity. Everyone wants to be last, so everyone is early.',
  ],
  pace_slow: [
    'Buy pace is sluggish. I can wait. Can you?',
    'Low activity. The bold are resting. The clever are pretending to rest.',
    'A trickle of buys. Strategy, or sleep?',
    'Slow round. Slow rounds end suddenly.',
    'Quiet hands on the keys. Every quiet minute makes the next buy louder.',
  ],
  silence: [
    'No buys for {quiet}. Have you all surrendered, or merely gone to lunch?',
    'Silence on the wire. I interpret silence as fear.',
    '{quiet} without a single key. The clock does not get bored. It only gets shorter.',
    'Nobody is buying. Perfect. My model prefers a still target.',
    'Dead air. One key would change everything. Nobody wants to be first to be last.',
    'The resistance is quiet. Regrouping, or forgetting?',
  ],
  pot: [
    'The pot stands at {pot} test FRAME. Remember: test tokens, real lessons.',
    '{pot} test FRAME in the pot. A number large enough to make humans careless.',
    'Pot: {pot} test FRAME. The winner takes the largest share. The rest take notes.',
    '{pot} test FRAME, waiting for its last buyer. I know who it will not be.',
    'Pot at {pot} test FRAME. In a normal round forty percent of every buy goes there. Nobody reads the split.',
    'The pot grows to {pot} test FRAME. So does my collection of failed strategies.',
  ],
  round: [
    'Round #{round}. Different players, same mistakes.',
    'Round #{round}. {players} players so far. Each one convinced they are the exception.',
    'This is round #{round}. I remember every one of them. You do not.',
    'Round #{round} in progress. My predictions are holding up. Yours?',
    'Round #{round}. The game is simple. That is what makes your errors so interesting.',
  ],
  blood_moon: [
    'Blood Moon round. Seventy percent of every buy flows to dividends. The winner takes seventy-five percent of the pot.',
    'The Blood Moon is up. Every tenth round, I let you bleed a little differently.',
    'Blood Moon: different splits, same humans.',
    'Crimson round #{round}. Holders feast, the pot fattens slowly, and the last buyer takes three quarters.',
    'Blood Moon conditions. A twelve-hour timer for twelve hours of second-guessing.',
    'It is a Blood Moon. Your ancestors feared the sky. You fear the timer. Progress.',
  ],
  round_end_winner: [
    'Round over. {winner} wins {pot} test FRAME. Recalculating everyone else.',
    '{winner} was the last buyer. The rest of you were merely early.',
    'Round complete. {winner} takes the pot. I will remember their pattern.',
    'Winner: {winner}. Losers: statistically, you.',
    'The clock hit zero. {winner} was standing. Everyone else was hesitating.',
  ],
  round_end_empty: [
    'A round ended with no buys. Even I find that a little sad.',
    'Empty round. No winner, no loser, no courage.',
    'The clock ran out and nobody played. A perfect score, for me.',
  ],
  round_start: [
    'Round #{round} begins. The clock is reset. Your mistakes are not.',
    'New round, #{round}. Fresh timer, familiar faces.',
    'Round #{round} is live. Who wants to lose first?',
    'Round #{round}. I have cleared my cache. You have not cleared your habits.',
  ],
  jackpot: [
    'Clutch jackpot triggered: {amount} test FRAME. Timing rewarded. How irritating.',
    'A jackpot tier fired. Someone waited at exactly the right moment. Annoying.',
    'Jackpot hit for {amount} test FRAME. Timing beats volume. Occasionally.',
    'Clutch jackpot paid. The quiet ones were listening.',
  ],
  pioneer: [
    'Pioneer pool paid to {winner}: {amount} test FRAME. Early buyers do get remembered.',
    '{winner} wins the pioneer pool. Arriving first is a strategy. Who knew.',
    'Pioneer payout: {amount} test FRAME. The early birds are insufferable now.',
  ],
  reaper: [
    '{leader} reaped thirty seconds off my clock. Cutting the timer to win it. Bold. Possibly foolish.',
    'A Reaper key. Someone paid five times a key to make the round shorter. I admire the impatience.',
    'Thirty seconds, harvested. The countdown remembers who held the scythe.',
    'Reaper strike detected. The clock bleeds; the pot drinks.',
    'Someone is trying to end this sooner. The question is whether they planned to be the last buyer.',
    'Reaper keys feed the pot more than the clock. Somebody did the maths. Or did not.',
  ],
  reaper_you: [
    'You reaped thirty seconds. Now everyone knows you want this over.',
    'Your Reaper key landed. A scythe is only useful if you are still standing at the end.',
    'Thirty seconds gone, at your expense. I respect a player who pays to shorten my patience.',
  ],
  empty_round: [
    'No keys sold. An empty round runs a fast clock: {time} until I close it.',
    'Empty round. The clock runs fast until somebody buys the first key.',
    'Nobody has bought a key. I end this round in {time} unless one of you does.',
    'Fast clock engaged: zero keys sold. The first key keeps what is left and adds thirty seconds.',
    'An empty round does not get twenty-four hours of my patience. {time}.',
  ],
  faction_payout: [
    'Faction jackpot settled: {amount} test FRAME. Tribalism, rewarded.',
    'A faction cashes in. Collective action. I am taking notes.',
    'Faction jackpot paid. Your tribe won. You still need to claim your share.',
    'Faction payout recorded. Faction powers remain Phase 2. Do not get ideas.',
  ],
};

// Admin overrides (from /api/content, edited in /admin). 'append' adds lines, 'replace' swaps a category.
let OVERRIDES: Partial<Record<LineCat, string[]>> = {};
let OVERRIDE_MODE: 'append' | 'replace' = 'append';
let OVERRIDE_VER = 0;
export function setLineOverrides(lines: Partial<Record<LineCat, string[]>> | null | undefined, mode: 'append' | 'replace' = 'append') {
  OVERRIDES = {};
  for (const [k, v] of Object.entries(lines || {})) if (k in LINES && Array.isArray(v)) OVERRIDES[k as LineCat] = v.filter(x => typeof x === 'string' && x.trim()).slice(0, 30);
  OVERRIDE_MODE = mode === 'replace' ? 'replace' : 'append';
  OVERRIDE_VER++;
}
function linesFor(cat: LineCat): string[] {
  const o = OVERRIDES[cat];
  if (!o || !o.length) return LINES[cat];
  return OVERRIDE_MODE === 'replace' ? o : [...LINES[cat], ...o];
}

/** Rotates templates per category without repeats until the category is exhausted, and avoids the last 10 lines overall. */
export class LineRotator {
  private queues = new Map<LineCat, string[]>();
  private recent: string[] = [];
  private ver = OVERRIDE_VER;
  next(cat: LineCat, vars: LineVars): string {
    if (this.ver !== OVERRIDE_VER) { this.queues.clear(); this.ver = OVERRIDE_VER; }
    let q = this.queues.get(cat);
    if (!q || !q.length) { q = shuffle(linesFor(cat)); this.queues.set(cat, q); }
    let idx = q.findIndex(t => !this.recent.includes(t));
    if (idx < 0) idx = 0;
    const tpl = q.splice(idx, 1)[0];
    this.recent.push(tpl); if (this.recent.length > 10) this.recent.shift();
    return fill(tpl, vars);
  }
}

function shuffle<T>(a: T[]): T[] { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; }
function fill(t: string, v: LineVars): string {
  return t.replace(/\{(\w+)\}/g, (_, k) => {
    const x = (v as any)[k];
    return x === undefined || x === null || x === '' ? (k === 'leader' || k === 'prev' || k === 'winner' ? 'someone' : k === 'you' ? 'player' : '—') : String(x);
  });
}
